/**
 * PDF/이미지 미리보기 팝업 — App(Shell/MobileShell)에 1회 마운트.
 *
 * 첨부 파일을 클릭하면(attachment-api 의 previewAttachment) 다운로드 대신 이 팝업이
 * 떠서 내용을 바로 보여준다. 팝업 안에 '저장(다운로드)' · '새 탭' · '닫기' 버튼 제공.
 * PDF/이미지/엑셀이 아닌 형식(예: 한글·zip)은 미리보기 대신 저장 안내.
 * v2.270.0 — 📊 엑셀(xlsx·xlsm·xls·csv)은 받아서 표로 그린다(`SheetPreview` · lazy).
 *
 * v2.52.0 — confirm 이 실린 미리보기(지출결재 제출 전 확인)는 아래에 확인 막대가 붙어
 * 이 화면에서 바로 [제출]/[돌아가서 수정] 을 고른다.
 *
 * v2.231.0 (점검 H-3) — PDF 는 **먼저 받아 보고** 그린다.
 *   전에는 iframe 에 서버 주소를 그대로 넣어, 서버가 PDF 만들기에 실패하면 오류 글자(JSON)가 미리보기에 그대로 찍히고
 *   ⬇ 저장을 누르면 그 오류 글자가 「.pdf」 파일로 저장됐다(✉ 메일·[제출]도 그대로 눌렸다).
 *   이제 받은 것이 PDF 가 아니면 오류 문구를 보여 주고 저장·메일·새 탭·확인 단추를 잠근다.
 *
 * v2.272.0 — 📱 폰(안드로이드·아이폰·아이패드)에서는 iframe 대신 pdf.js 로 쪽마다 직접 그린다(`PdfCanvasView` · lazy).
 *   iframe 의 브라우저 PDF 보기가 안드로이드 크롬엔 없고(빈 칸) 아이폰은 첫 쪽만 보였다. 판정은 lib/pdf-inline.ts.
 */

import { lazy, Suspense, useEffect, useState, useSyncExternalStore } from 'react';
import {
  subscribePdfPreview,
  getPdfPreviewState,
  closePdfPreview,
} from '../lib/pdf-preview';
// ⭐✉ 단추와 「형식 고르기(▾)」는 공용 `MailSendButton` 이 갖고 있다 (v2.175.0).
//   견적서 미리보기·발주서 미리보기 창도 같은 것을 쓴다 — 두 벌로 적으면 반드시 갈린다.
import { MailSendButton } from './MailSendButton';
import { useDialogLayer } from '../lib/dialog-stack';
import { needsCanvasPdf } from '../lib/pdf-inline';

// 📊 엑셀 미리보기 (v2.270.0) — SheetJS 가 무거워 엑셀을 열 때만 받는다
const SheetPreview = lazy(() => import('./SheetPreview'));
// 📱 폰 PDF 미리보기 (v2.272.0) — pdf.js 가 무거워 폰에서 PDF 를 열 때만 받는다(PC 는 받지 않는다)
const PdfCanvasView = lazy(() => import('./PdfCanvasView'));
/** 이 기기는 PDF 를 pdf.js 로 그려야 하나 — 기기는 안 바뀌니 한 번만 잰다 */
const CANVAS_PDF = typeof navigator !== 'undefined' && needsCanvasPdf(navigator);

type LoadState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ok'; src: string }
  | { kind: 'error'; message: string };

/** 서버가 PDF 대신 돌려준 오류 본문에서 사람이 읽을 문구를 꺼낸다 */
function errorTextOf(status: number, body: string): string {
  try {
    const j = JSON.parse(body) as { error?: { message?: string } | string; message?: string };
    const msg = typeof j.error === 'object' ? j.error?.message : j.message ?? (typeof j.error === 'string' ? j.error : undefined);
    if (msg) return msg;
  } catch {
    /* JSON 이 아니다 — 아래 기본 문구 */
  }
  return `서버 응답 ${status}`;
}

export function PdfPreviewModal() {
  const st = useSyncExternalStore(subscribePdfPreview, getPdfPreviewState, getPdfPreviewState);
  const [busy, setBusy] = useState(false);
  const [load, setLoad] = useState<LoadState>({ kind: 'idle' });
  const [retry, setRetry] = useState(0);
  const [imgFailed, setImgFailed] = useState(false);

  // ESC — 보기 창이라 닫는다(처리 중이면 그대로). 맨 위 창만 받아서, 입력 창의 첨부를 미리 보다 ESC 를 눌러도
  //   아래 입력 창까지 닫히지 않는다 (v2.231.0 · H-4 · lib/dialog-stack.ts)
  useDialogLayer(st.open, busy ? null : closePdfPreview);

  // 새 미리보기가 열리면 진행중 표시는 초기화
  // (열어 둔 채 다른 문서로 바뀔 때 형식 메뉴도 닫혀야 한다 → ✉ 단추에 key 를 줘 다시 그린다)
  useEffect(() => { if (st.open) { setBusy(false); setImgFailed(false); } }, [st.open, st.url]);

  // PDF 는 먼저 받아서 「정말 PDF 인가」를 본 뒤 그린다 (v2.231.0 · H-3)
  const isPdfPreview = st.open && st.canPreview && !st.isImage && !st.isSheet;
  useEffect(() => {
    if (!isPdfPreview) { setLoad({ kind: 'idle' }); return; }
    // 화면이 이미 만든 파일(blob:)은 받아 볼 필요가 없다
    if (st.url.startsWith('blob:') || st.url.startsWith('data:')) { setLoad({ kind: 'ok', src: st.url }); return; }
    const ac = new AbortController();
    let made: string | null = null;
    setLoad({ kind: 'loading' });
    void (async () => {
      try {
        const res = await fetch(st.url, { credentials: 'include', signal: ac.signal });
        if (!res.ok) {
          const body = await res.text().catch(() => '');
          setLoad({ kind: 'error', message: errorTextOf(res.status, body) });
          return;
        }
        const ct = (res.headers.get('content-type') ?? '').toLowerCase();
        const blob = await res.blob();
        // 형식 머리글이 pdf 가 아니어도(옛 첨부는 octet-stream 일 수 있다) 첫 글자가 %PDF- 면 PDF 다
        const looksPdf = ct.includes('pdf') || (await blob.slice(0, 5).text()) === '%PDF-';
        if (!looksPdf) {
          const body = ct.includes('json') ? await blob.text().catch(() => '') : '';
          setLoad({ kind: 'error', message: body ? errorTextOf(res.status, body) : 'PDF 가 아닌 응답을 받았습니다' });
          return;
        }
        made = URL.createObjectURL(ct.includes('pdf') ? blob : new Blob([blob], { type: 'application/pdf' }));
        setLoad({ kind: 'ok', src: made });
      } catch (e) {
        if (ac.signal.aborted) return;
        setLoad({ kind: 'error', message: (e as Error).message || '네트워크 오류' });
      }
    })();
    return () => {
      ac.abort();
      if (made) URL.revokeObjectURL(made);
    };
  }, [isPdfPreview, st.url, retry]);

  if (!st.open) return null;

  const confirm = st.confirm;
  // PDF 를 못 받았으면(또는 아직 받는 중이면) 저장·메일·새 탭·확인을 잠근다 — 오류 글자가 「.pdf」로 나가지 않게
  const blocked = (isPdfPreview && load.kind !== 'ok') || (st.isImage && imgFailed);
  const failed = (isPdfPreview && load.kind === 'error') || (st.isImage && imgFailed);

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/50 flex flex-col p-2 sm:p-6"
    >
      <div
        className={`bg-white rounded shadow-2xl w-full ${st.isSheet ? 'max-w-7xl' : 'max-w-5xl'} mx-auto flex flex-col flex-1 min-h-0`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* 헤더 */}
        <div className="px-4 py-2.5 border-b border-slate-200 flex items-center justify-between gap-3 flex-shrink-0">
          <div className="font-medium text-slate-800 text-sm truncate" title={st.filename}>
            {st.isSheet ? '📊' : '📄'} {st.filename}
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {/* ✉ 메일 — 이 문서를 첨부한 메일 작성 (v2.108.0 · v2.250.0 부터 viewer 도 — 금액 문서 단추만 MailSendButton 이 뺀다).
                v2.112.0: 같은 문서를 형식이 여럿으로 만들 수 있으면(견적서 PDF·엑셀,
                기술지원확약서 PDF·워드) **누를 때 고른다**. 예전에는 전부 붙여 놓고
                필요 없는 쪽을 ×로 빼야 했다 (2026-09-01 사용자 지시). */}
            {st.mailDoc && (
              <MailSendButton
                key={st.url}
                compose={() => st.mailDoc!}
                disabled={busy || blocked}
                label="✉ 메일로 보내기"
                // 작성 모달(z-90)이 미리보기(z-80) 아래 깔리지 않게 닫고 연다
                onBeforeOpen={closePdfPreview}
                className="px-2.5 py-1 text-xs bg-sky-600 hover:bg-sky-700 text-white rounded disabled:opacity-50"
              />
            )}
            {st.noDownload ? null : blocked ? (
              <span
                data-pdf-save-blocked
                title={failed ? 'PDF 를 만들지 못해 저장할 수 없습니다' : 'PDF 를 받는 중입니다'}
                className="px-2.5 py-1 text-xs bg-emerald-600 text-white rounded opacity-40 cursor-not-allowed select-none"
              >
                ⬇ {st.downloadLabel ?? '저장'}
              </span>
            ) : (
              <a
                href={st.downloadUrl}
                download={st.filename}
                className="px-2.5 py-1 text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded"
              >
                ⬇ {st.downloadLabel ?? '저장'}
              </a>
            )}
            {/* 엑셀은 새 탭에서 열면 그냥 내려받아진다 — 단추를 두지 않는다 */}
            {!st.isSheet && (
              <button
                type="button"
                onClick={() => window.open(st.url, '_blank', 'noopener')}
                disabled={blocked}
                className="btn btn-secondary btn-sm"
              >
                🔗 새 탭
              </button>
            )}
            <button
              type="button"
              onClick={closePdfPreview}
              disabled={busy}
              className="btn btn-secondary btn-sm"
            >
              닫기 ✕
            </button>
          </div>
        </div>

        {/* 본문 */}
        <div className={`flex-1 min-h-0 bg-slate-100 overflow-hidden ${confirm ? '' : 'rounded-b'}`}>
          {st.canPreview ? (
            st.isSheet ? (
              <Suspense fallback={<div className="h-full flex items-center justify-center text-slate-500 text-sm">엑셀을 읽는 중…</div>}>
                <SheetPreview key={st.url} url={st.url} filename={st.filename} mime={st.mime} />
              </Suspense>
            ) : st.isImage ? (
              imgFailed ? (
                <div data-pdf-preview-error className="h-full flex flex-col items-center justify-center text-center text-rose-700 text-sm gap-2 p-6">
                  <div className="text-3xl">⚠</div>
                  <div>그림을 불러오지 못했습니다 — 파일이 지워졌거나 권한이 없을 수 있습니다.</div>
                </div>
              ) : (
                <div className="w-full h-full overflow-auto flex items-center justify-center p-2">
                  <img
                    src={st.url}
                    alt={st.filename}
                    onError={() => setImgFailed(true)}
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
              )
            ) : load.kind === 'ok' ? (
              CANVAS_PDF ? (
                <Suspense fallback={<div className="h-full flex items-center justify-center text-slate-500 text-sm">PDF 를 그리는 중…</div>}>
                  <PdfCanvasView key={load.src} src={load.src} />
                </Suspense>
              ) : (
                <iframe src={load.src} title={st.filename} className="w-full h-full border-0" />
              )
            ) : load.kind === 'error' ? (
              <div data-pdf-preview-error className="h-full flex flex-col items-center justify-center text-center text-sm gap-3 p-6">
                <div className="text-3xl">⚠</div>
                <div className="font-semibold text-rose-700">PDF 를 만들지 못했습니다</div>
                <div className="text-slate-600 max-w-lg whitespace-pre-wrap break-words">{load.message}</div>
                <div className="text-xs text-slate-500">저장·메일 단추는 잠가 두었습니다. 잠시 뒤 다시 해 보고, 계속되면 관리자에게 알려 주세요.</div>
                <button
                  type="button"
                  onClick={() => setRetry((n) => n + 1)}
                  className="btn btn-secondary btn-sm"
                >
                  ↻ 다시 시도
                </button>
              </div>
            ) : (
              <div className="h-full flex items-center justify-center text-slate-500 text-sm">
                PDF 를 불러오는 중…
              </div>
            )
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-center text-slate-500 text-sm gap-3 p-6">
              <div className="text-4xl">📎</div>
              <div>이 형식은 화면 미리보기를 지원하지 않습니다.</div>
              <a
                href={st.downloadUrl}
                download={st.filename}
                className="px-3 py-1.5 text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded"
              >
                ⬇ 파일 저장
              </a>
            </div>
          )}
        </div>

        {/* 확인 막대 — 미리보기를 본 자리에서 바로 결정 (v2.52.0) */}
        {confirm && (
          <div className="flex items-center justify-between gap-3 flex-wrap px-4 py-2.5 border-t border-slate-200 bg-slate-50 rounded-b flex-shrink-0">
            <div className="text-xs text-slate-600">{confirm.message ?? ''}</div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                type="button"
                onClick={closePdfPreview}
                disabled={busy}
                className="btn btn-secondary"
              >
                {confirm.cancelLabel ?? '돌아가서 수정'}
              </button>
              <button
                type="button"
                disabled={busy || blocked}
                onClick={async () => {
                  setBusy(true);
                  try {
                    await confirm.onConfirm();
                  } finally {
                    setBusy(false);
                  }
                }}
                className="btn btn-primary"
              >
                {busy ? '처리 중…' : confirm.confirmLabel}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
